import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  DataGrid,
  // GridOverlay
} from "@material-ui/data-grid";
import TablePagination from "@material-ui/core/TablePagination";
// import LinearProgress from "@material-ui/core/LinearProgress";
import { makeStyles } from "@material-ui/core";
import PropTypes from "prop-types";
import { isEqual } from "lodash";
import { useUtils } from "../../utils";

const useStyles = makeStyles({
  root: {
    boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px",
    borderRadius: 10,
    border: 0,
  },
});

function DataTable(props) {
  const {
    columns,
    apiEndpoint,
    pageSizeOptions = [10, 25, 50, 100],
    onRowClick = () => {},
    transformRowData = (data) => data,
    isServerSideSorting = false,
    isServerSidePagination = false,
    service,
    defaultSortModel = [],
    apiParams,
  } = props;
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pageSizeOptions[0]);
  const [sortModel, setSortModel] = useState(defaultSortModel);
  const { showToast } = useUtils();

  const lastRequestBody = useRef();

  const fetchData = useCallback(async (apiService, apiURL, body, isServerSidePagination) => {
    try {
      setLoading(true);
      const response = await apiService.post(apiURL, {
        ...body,
      });
      const { list, totalRows } = response;
      setRows(list);
      setTotalCount(!isServerSidePagination ? list?.length : totalRows);
    } catch (error) {
      showToast("error", error?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const body = { ...apiParams };
    if (isServerSidePagination) {
      body.limit = rowsPerPage;
      body.page = page;
    }
    if (isServerSideSorting) {
      body.sort_field = sortModel[0]?.field;
      body.sort_order = sortModel[0]?.sort;
    }
    const isSameRequest = isEqual(body, lastRequestBody.current);
    if (!isSameRequest) {
      fetchData(service, apiEndpoint, body, isServerSidePagination).then(() => {
        lastRequestBody.current = body;
      });
    }
  }, [fetchData, service, apiEndpoint, page, rowsPerPage, sortModel, isServerSideSorting, isServerSidePagination, apiParams]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedRows = useMemo(() => {
    if (isServerSideSorting) {
      return rows;
    }
    // Client-side sorting
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      return [...rows].sort((a, b) => {
        if (a[field] < b[field]) return sort === "asc" ? -1 : 1;
        if (a[field] > b[field]) return sort === "asc" ? 1 : -1;
        return 0;
      });
    }
    return rows;
  }, [rows, sortModel, isServerSideSorting]);

  const transformedData = useMemo(() => sortedRows.map(transformRowData), [sortedRows, transformRowData]);

  const finalColumns = useMemo(() => columns.map((col) => ({ ...col, flex: 1, resizable: true })), [columns]);

  return (
    <React.Fragment>
      <DataGrid
        rows={transformedData}
        columns={finalColumns}
        pageSize={rowsPerPage}
        page={page}
        rowCount={totalCount}
        autoHeight
        loading={loading}
        disableSelectionOnClick
        density="standard"
        onRowClick={(params) => onRowClick(params.row)}
        components={{
          Pagination: () => <CustomPagination {...{ pageSizeOptions, totalCount, page, rowsPerPage, handleChangeRowsPerPage, handleChangePage }} />,
          // LoadingOverlay: () => (
          //   <GridOverlay>
          //     <div style={{ position: "absolute", top: 0, bottom: 0, width: "100%", backgroundColor: "red" }}>
          //       <LinearProgress />
          //     </div>
          //   </GridOverlay>
          // ),
        }}
        sortModel={sortModel}
        onSortModelChange={(model) => {
          setSortModel(model);
        }}
        extendRowFullWidth
        disableColumnMenu
        sortingOrder={["desc", "asc"]}
        sortingMode={isServerSideSorting ? "server" : "client"}
        paginationMode={isServerSidePagination ? "server" : "client"}
        className={classes.root}
      />
    </React.Fragment>
  );
}

const CustomPagination = ({ pageSizeOptions, totalCount, page, rowsPerPage, handleChangeRowsPerPage, handleChangePage }) => (
  <TablePagination
    component="div"
    rowsPerPageOptions={pageSizeOptions}
    count={totalCount}
    page={page}
    onPageChange={handleChangePage}
    rowsPerPage={rowsPerPage}
    onRowsPerPageChange={handleChangeRowsPerPage}
  />
);

DataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  apiEndpoint: PropTypes.string.isRequired,
  pageSizeOptions: PropTypes.array,
  defaultSortModel: PropTypes.array,
  onRowClick: PropTypes.func,
  transformRowData: PropTypes.func,
  isServerSideSorting: PropTypes.bool,
  isServerSidePagination: PropTypes.bool,
};

export default React.memo(DataTable);
